import { IconCheck, IconCoin, IconGavel } from "@tabler/icons-react"
import { type FC } from "react"

import { contractWithdrawalPeriodDays } from "@forento/core/models/contractWithdrawal"
import { toPriceString } from "@forento/core/utilities/currency"
import { toDateString, toDateTimeString } from "@forento/core/utilities/date"
import { cn } from "@forento/ui/utilities/tailwind"

import Button from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import PartialLoadingPage from "~/components/PartialLoadingPage"
import { useAlert } from "~/contexts/AlertContext"
import { useToast } from "~/contexts/ToastContext"
import { usePlatform, useUser } from "~/contexts/UserContext"
import { query } from "~/utilities/trpc"

function toAmountLabel(withdrawal: { amount: number | null; currency: string | null }): string | null {
	if (withdrawal.amount === null || withdrawal.currency === null) return null
	return toPriceString({ amount: withdrawal.amount, currency: withdrawal.currency })
}

const ContractWithdrawals: FC = () => {
	const alert = useAlert()
	const toast = useToast()
	const { reload } = useUser()
	const platform = usePlatform()!

	const enableDate = platform.contractWithdrawalEnableDate
	const isEnabled = enableDate !== null

	const setEnabled = query.platform.setContractWithdrawalEnabled.useMutation()
	const withdrawals = query.contractWithdrawal.listReceived.useQuery(undefined, { enabled: isEnabled })
	const setProcessed = query.contractWithdrawal.setProcessed.useMutation()
	const refund = query.contractWithdrawal.refund.useMutation()

	async function handleToggle(newValue: boolean) {
		if (newValue) {
			const result = await alert.confirm(
				"Turn on the withdrawal function",
				"Buyers of courses, videos, downloadables and digital-only bundles will have to tick a box confirming they want immediate access and lose their right of withdrawal, and cannot complete a purchase without it. Have your legal counsel review the wording before turning this on.",
				{ confirmLabel: "Turn on", isDestructive: false },
			)
			if (!result.result) return
			result.close()
		}

		try {
			await setEnabled.mutateAsync({ isEnabled: newValue })
			await reload()
			toast.showChangesSaved("Withdrawal function")
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to save the withdrawal function. Please try again later.")
		}
	}

	async function handleSetProcessed(id: number, isProcessed: boolean) {
		try {
			await setProcessed.mutateAsync({ id, isProcessed })
			await withdrawals.refetch()
		} catch (error) {
			console.error(error)
			await alert.show("Error", "Failed to update the withdrawal. Please try again later.")
		}
	}

	async function handleRefund(withdrawal: {
		id: number
		productName: string
		amount: number | null
		currency: string | null
	}) {
		const result = await alert.confirm(
			"Refund this contract",
			`This refunds ${toAmountLabel(withdrawal) ?? "the full amount"} for **${withdrawal.productName}** through your payment gateway.`,
		)
		if (!result.result) return

		try {
			await refund.mutateAsync({ id: withdrawal.id })
			result.close()
			await withdrawals.refetch()
			toast.showSuccess("Refund sent.")
		} catch (error) {
			console.error(error)
			result.close()
			await alert.show(
				"Refund failed",
				error instanceof Error ? error.message : "Failed to refund. Please try again later.",
			)
		}
	}

	return (
		<section className="border-component-border bg-component-background rounded-3xl border p-6">
			<div className="flex min-w-0 items-start gap-3">
				<div className="bg-primary/10 flex size-11 shrink-0 items-center justify-center rounded-lg">
					<IconGavel className="text-primary size-5" />
				</div>
				<div className="min-w-0">
					<h2 className="text-lg font-semibold">Withdrawal function</h2>
					<p className="text-sm text-gray-500">
						EU consumers can withdraw from a distance contract within {contractWithdrawalPeriodDays} days.
						Since 19 June 2026, Directive (EU) 2023/2673 requires you to offer that withdrawal as an online
						function if you sell to them. Turning this on shows a "Withdraw from contract here" button to
						students on your academy and emails them an acknowledgement when they use it.
					</p>
				</div>
			</div>
			<Checkbox
				className="mt-5"
				label="Show the withdrawal function to students"
				isChecked={isEnabled}
				onChange={handleToggle}
				isDisabled={setEnabled.isPending}
			/>
			{enableDate !== null && (
				<p className="mt-2 text-sm text-gray-500">Offered since {toDateString(enableDate)}.</p>
			)}
			{isEnabled && (
				<>
					<div className="mt-5 rounded-lg bg-[#f9f8ff] p-4">
						<h3 className="text-base font-semibold">What stays refundable</h3>
						<p className="mt-2 text-sm text-gray-500">
							Checkout asks buyers to consent to immediate access, so courses, videos, downloadables and
							digital-only bundles drop out of the withdrawal function as soon as they consent, under the
							digital content exception in Article 16(1)(m). Events on a fixed date are exempt without any
							consent. Sessions and memberships keep their withdrawal right for the full{" "}
							{contractWithdrawalPeriodDays} days.
						</p>
					</div>
					<div className="mt-5 border-t border-[#efefef] pt-5">
						<h3 className="text-base font-semibold">Received withdrawals</h3>
						<p className="mt-1 text-sm text-gray-500">
							Reimburse all payments for a withdrawn contract no later than {contractWithdrawalPeriodDays}{" "}
							days after you received the withdrawal. Refunds are issued in Stripe or PayPal.
						</p>
						{withdrawals.error ? (
							<p className="mt-4">Failed to load withdrawals.</p>
						) : withdrawals.data === undefined ? (
							<PartialLoadingPage />
						) : withdrawals.data.length === 0 ? (
							<p className="mt-4 text-sm text-gray-500">No withdrawals received yet.</p>
						) : (
							<ul className="mt-4 flex flex-col gap-3">
								{withdrawals.data.map(withdrawal => (
									<li
										key={withdrawal.id}
										className="flex flex-wrap items-start justify-between gap-4 rounded-lg border border-[#efefef] p-4"
									>
										<div className="min-w-0">
											<p className="font-semibold">{withdrawal.productName}</p>
											<p className="text-sm text-gray-500">
												{withdrawal.student.name} ({withdrawal.confirmationEmail})
												{withdrawal.amount !== null && withdrawal.currency !== null && (
													<>
														{" "}
														&middot;{" "}
														{toPriceString({
															amount: withdrawal.amount,
															currency: withdrawal.currency,
														})}
													</>
												)}{" "}
												&middot; bought {toDateString(withdrawal.purchaseDate)}
											</p>
											<p className="text-sm text-gray-500">
												Withdrew {toDateTimeString(withdrawal.submitDate)}
											</p>
											{withdrawal.reason !== null && (
												<p className="mt-2 text-sm">Reason: {withdrawal.reason}</p>
											)}
										</div>
										<div className="flex shrink-0 items-center gap-3">
											<span
												className={cn(
													"rounded-full px-3 py-1 text-xs font-semibold",
													withdrawal.processDate === null
														? "bg-amber-100 text-amber-900"
														: "bg-primary/10 text-primary",
												)}
											>
												{withdrawal.processDate === null ? "Awaiting refund" : "Refunded"}
											</span>
											{withdrawal.processDate === null ? (
												<>
													<Button
														variant="primary"
														onClick={() => handleRefund(withdrawal)}
														isDisabled={refund.isPending}
													>
														<IconCoin className="size-4" />
														Refund
													</Button>
													<Button
														variant="secondary"
														onClick={() => handleSetProcessed(withdrawal.id, true)}
														isDisabled={setProcessed.isPending}
													>
														<IconCheck className="size-4" />
														Mark as refunded
													</Button>
												</>
											) : (
												!withdrawal.isRefundedThroughForento && (
													<Button
														variant="secondary"
														onClick={() => handleSetProcessed(withdrawal.id, false)}
														isDisabled={setProcessed.isPending}
													>
														Undo
													</Button>
												)
											)}
										</div>
									</li>
								))}
							</ul>
						)}
					</div>
				</>
			)}
		</section>
	)
}

export default ContractWithdrawals
